import type { SVGProps } from "react";

import { cn } from "@/lib/utils";

export type GridConnectorType =
  | "plus"
  | "end"
  | "direction"
  | "circle"
  | "arrow";

export type GridConnectorCorner =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

/**
 * Semantic glyph names — junction at the SVG centre, arms pointing inward.
 * Prefer these over `type` + `rotation` when placing connectors on a box corner
 * or rail intersection.
 *
 * - `t`  — ⊤  full top bar + stem down
 * - `tl` — ┌  arms right + down
 * - `tr` — ┐  arms left + down
 * - `bl` — └  arms right + up
 * - `br` — ┘  arms left + up
 * - `plus` — symmetric +
 */
export type GridConnectorShape = "plus" | "t" | "tl" | "tr" | "bl" | "br";

type GridConnectorSharedProps = Omit<
  SVGProps<SVGSVGElement>,
  "children" | "width" | "height"
>;

type GridConnectorByShape = GridConnectorSharedProps & {
  shape: GridConnectorShape;
  type?: never;
  rotation?: never;
};

type GridConnectorByType = GridConnectorSharedProps & {
  shape?: never;
  type?: GridConnectorType;
  /** 0 | 90 | 180 | 270 — applied as an SVG transform on the glyph */
  rotation?: 0 | 90 | 180 | 270;
};

export type GridConnectorProps = GridConnectorByShape | GridConnectorByType;

/** Figma "Grid Connectors" (node 3593:8963) — #0C0D0D, 1px stroke. */
const STROKE_WIDTH = 1;

/** Shared pointer/color defaults — merge with position helpers below. */
export const GRID_CONNECTOR_BASE = "text-grey-950 pointer-events-none" as const;

/** GridTee (⊤) — junction at viewBox (3.5, 0.5) lands on the parent corner. */
export const GRID_TEE_TOP_LEFT = `${GRID_CONNECTOR_BASE} absolute -top-[0.5px] -left-[3.5px]`;
export const GRID_TEE_TOP_RIGHT = `${GRID_CONNECTOR_BASE} absolute -top-[0.5px] -right-[3.5px]`;

/**
 * GridTee (⊤) on the top corner of a panel with a 1px side border, where the
 * tee's bar covers the 1px divider directly above and its stem covers the
 * side border. Offsets are measured from the padding box, so both land on the
 * border centres instead of half a pixel inside.
 */
export const GRID_TEE_FRAME_TOP_LEFT = `${GRID_CONNECTOR_BASE} absolute -top-px -left-1`;
export const GRID_TEE_FRAME_TOP_RIGHT = `${GRID_CONNECTOR_BASE} absolute -top-px -right-1`;

/** GridElbow at a page-rail ↔ frame-border intersection (centre on the corner pixel). */
export const GRID_ELBOW_RAIL_TOP_LEFT = `${GRID_CONNECTOR_BASE} absolute -top-px -left-px -translate-x-1/2 -translate-y-1/2`;
export const GRID_ELBOW_RAIL_TOP_RIGHT = `${GRID_CONNECTOR_BASE} absolute -top-px -right-px translate-x-1/2 -translate-y-1/2`;
export const GRID_ELBOW_RAIL_BOTTOM_LEFT = `${GRID_CONNECTOR_BASE} absolute -bottom-px -left-px -translate-x-1/2 translate-y-1/2`;
export const GRID_ELBOW_RAIL_BOTTOM_RIGHT = `${GRID_CONNECTOR_BASE} absolute -right-px -bottom-px translate-x-1/2 translate-y-1/2`;

/** GridElbow hugging a framed panel corner (arms on the border edges). */
export const GRID_ELBOW_FRAME_TOP_LEFT = `${GRID_CONNECTOR_BASE} absolute -top-[0.5px] -left-[0.5px]`;
export const GRID_ELBOW_FRAME_TOP_RIGHT = `${GRID_CONNECTOR_BASE} absolute -top-[0.5px] -right-[0.5px]`;
export const GRID_ELBOW_FRAME_BOTTOM_LEFT = `${GRID_CONNECTOR_BASE} absolute -bottom-[0.5px] -left-[0.5px]`;
export const GRID_ELBOW_FRAME_BOTTOM_RIGHT = `${GRID_CONNECTOR_BASE} absolute -right-[0.5px] -bottom-[0.5px]`;

/** GridTeeBottom (⊥) — crossbar on bottom edge, stem pointing up. */
export const GRID_TEE_BOTTOM_CENTER = `${GRID_CONNECTOR_BASE} absolute -bottom-[0.5px] left-1/2 -translate-x-1/2`;

/** Shared SVG attrs for path-based junction glyphs (GridCross, GridTee, GridElbow). */
const PATH_GLYPH_PROPS = {
  fill: "none" as const,
  stroke: "currentColor" as const,
  strokeWidth: STROKE_WIDTH,
  shapeRendering: "crispEdges" as const,
  overflow: "visible" as const,
};

const TYPE_SIZE: Record<GridConnectorType, { w: number; h: number }> = {
  plus: { w: 7, h: 7 },
  end: { w: 7, h: 4 },
  direction: { w: 4, h: 4 },
  circle: { w: 7, h: 7 },
  arrow: { w: 7, h: 7 },
};

const SHAPE_RESOLVE: Record<
  GridConnectorShape,
  { type: GridConnectorType; rotation: 0 | 90 | 180 | 270 }
> = {
  plus: { type: "plus", rotation: 0 },
  t: { type: "end", rotation: 180 },
  tl: { type: "direction", rotation: 90 },
  tr: { type: "direction", rotation: 180 },
  bl: { type: "direction", rotation: 0 },
  br: { type: "direction", rotation: 270 },
};

type ResolvedGlyph = {
  type: GridConnectorType;
  rotation: 0 | 90 | 180 | 270;
};

function resolveProps(props: GridConnectorProps): ResolvedGlyph {
  if ("shape" in props && props.shape !== undefined) {
    return SHAPE_RESOLVE[props.shape];
  }
  return {
    type: props.type ?? "plus",
    rotation: props.rotation ?? 0,
  };
}

/**
 * Figma rail primitives (`plus`, `end`, `direction`, `circle`, `arrow`) at 1px
 * stroke. Use `shape` for centre-anchored semantic placement (`t`, `tl`, …).
 *
 * For 4px junction crosses and 7×4 ⊤ tees, use `<GridCross />` / `<GridTee />`
 * — they keep their own 1px path geometry and must not be approximated via
 * `type` + `rotation`.
 */
export function GridConnector(props: GridConnectorProps) {
  const { className, ...rest } = props;
  const { type, rotation } = resolveProps(props);
  const size = TYPE_SIZE[type];

  return (
    <svg
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_WIDTH}
      strokeLinecap="square"
      overflow="visible"
      className={cn("text-grey-400 shrink-0", className)}
      aria-hidden="true"
      {...rest}
    >
      <g transform={rotateTransform(rotation, size.w, size.h)}>
        {renderGlyph(type)}
      </g>
    </svg>
  );
}

function rotateTransform(rot: 0 | 90 | 180 | 270, w: number, h: number) {
  if (rot === 0) return undefined;
  const cx = w / 2;
  const cy = h / 2;
  return `rotate(${rot} ${cx} ${cy})`;
}

function renderGlyph(type: GridConnectorType) {
  switch (type) {
    case "plus":
      return (
        <>
          <line x1="0" y1="3.5" x2="7" y2="3.5" />
          <line x1="3.5" y1="0" x2="3.5" y2="7" />
        </>
      );
    case "end":
      return (
        <>
          <line x1="0" y1="4" x2="7" y2="4" />
          <line x1="3.5" y1="0" x2="3.5" y2="4" />
        </>
      );
    case "direction":
      return (
        <>
          <line x1="0" y1="4" x2="4" y2="4" />
          <line x1="0" y1="0" x2="0" y2="4" />
        </>
      );
    case "circle":
      return <circle cx="3.5" cy="3.5" r="3" />;
    case "arrow":
      return (
        <>
          <line x1="3.5" y1="0" x2="3.5" y2="7" />
          <polyline points="1,2 3.5,0 6,2" />
        </>
      );
  }
}

/**
 * Exact Figma "Grid Connectors" glyphs (node Line 603), #0C0D0D 1px.
 *
 * 4px corner cross: two long arms tracing the box edges inward, two short stubs
 * poking out. Used at section dividers / box corners — not an L-bracket.
 */
const CROSS_PATH: Record<GridConnectorCorner, string> = {
  "top-left": "M0 0.5H0.5M4 0.5L0.5 0.5M0.5 0.5L0.5 4M0.5 0.5L0.5 0",
  "top-right": "M4 0.5H3.5M0 0.5L3.5 0.5M3.5 0.5L3.5 4M3.5 0.5L3.5 0",
  "bottom-left": "M0 3.5H0.5M4 3.5L0.5 3.5M0.5 3.5L0.5 0M0.5 3.5L0.5 4",
  "bottom-right": "M4 3.5H3.5M0 3.5L3.5 3.5M3.5 3.5L3.5 0M3.5 3.5L3.5 4",
};

export function GridCross({
  corner,
  className,
}: {
  corner: GridConnectorCorner;
  className?: string;
}) {
  return (
    <svg
      width="4"
      height="4"
      viewBox="0 0 4 4"
      aria-hidden="true"
      className={cn(GRID_CONNECTOR_BASE, className)}
      {...PATH_GLYPH_PROPS}
    >
      <path d={CROSS_PATH[corner]} />
    </svg>
  );
}

const GRID_TEE_PATH =
  "M0 0.5L3.5 0.5M7 0.5L3.5 0.5M3.5 0.5L3.5 4M3.5 0.5L3.5 0";
const GRID_TEE_BOTTOM_PATH =
  "M0 3.5L3.5 3.5M7 3.5L3.5 3.5M3.5 3.5L3.5 0M3.5 3.5L3.5 4";

/** 7px wide ⊤: full top bar + stem pointing down. */
export function GridTee({ className }: { className?: string }) {
  return (
    <svg
      width="7"
      height="4"
      viewBox="0 0 7 4"
      aria-hidden="true"
      className={cn(GRID_CONNECTOR_BASE, className)}
      {...PATH_GLYPH_PROPS}
    >
      <path d={GRID_TEE_PATH} />
    </svg>
  );
}

/** 7px wide ⊥: full bottom bar + stem pointing up. */
export function GridTeeBottom({ className }: { className?: string }) {
  return (
    <svg
      width="7"
      height="4"
      viewBox="0 0 7 4"
      aria-hidden="true"
      className={cn(GRID_CONNECTOR_BASE, className)}
      {...PATH_GLYPH_PROPS}
    >
      <path d={GRID_TEE_BOTTOM_PATH} />
    </svg>
  );
}

/** 4px L-bracket at a box corner — path-based, matches GridCross / GridTee weight. */
const ELBOW_PATH: Record<GridConnectorCorner, string> = {
  "top-left": "M0 0.5H4M0.5 0V4",
  "top-right": "M0 0.5H4M3.5 0V4",
  "bottom-left": "M0 3.5H4M0.5 0V4",
  "bottom-right": "M0 3.5H4M3.5 0V4",
};

export function GridElbow({
  corner,
  className,
}: {
  corner: GridConnectorCorner;
  className?: string;
}) {
  return (
    <svg
      width="4"
      height="4"
      viewBox="0 0 4 4"
      aria-hidden="true"
      className={cn(GRID_CONNECTOR_BASE, className)}
      {...PATH_GLYPH_PROPS}
    >
      <path d={ELBOW_PATH[corner]} />
    </svg>
  );
}

interface GridCornerMarksProps {
  /** Glyph type for all four corners */
  type?: GridConnectorType;
  /** Which corners to render. Defaults to all four. */
  corners?: GridConnectorCorner[];
  /** Extra className for each marker (e.g. `text-grey-500`) */
  markClassName?: string;
  /**
   * Offset from the parent's edge, in pixels. Positive values push markers
   * outside the parent box (so the center of the glyph lands on the corner).
   * Default `half the glyph size` so the cross is perfectly centered on the corner.
   */
  offset?: number;
}

/**
 * Renders `<GridConnector>` glyphs at the four corners of a parent box.
 * The parent MUST have `position: relative` (or similar) so the absolute
 * children anchor correctly.
 */
export function GridCornerMarks({
  type = "plus",
  corners = ["top-left", "top-right", "bottom-left", "bottom-right"],
  markClassName,
  offset,
}: GridCornerMarksProps) {
  if (type === "direction") {
    return (
      <>
        {corners.map((corner) => (
          <GridElbow
            key={corner}
            corner={corner}
            className={cn(
              "absolute",
              markClassName,
              cornerElbowPosition(corner)
            )}
          />
        ))}
      </>
    );
  }

  const size = TYPE_SIZE[type];
  const off = offset ?? Math.round(size.w / 2);

  return (
    <>
      {corners.map((corner) => (
        <GridConnector
          key={corner}
          type={type}
          rotation={CORNER_ROTATION[corner]}
          className={cn("absolute", markClassName)}
          style={cornerStyle(corner, off)}
        />
      ))}
    </>
  );
}

/**
 * Rotation per corner, assuming the base `direction` glyph renders as └
 * (bottom-left L). Rotations align each glyph so its arms hug the corner
 * of the parent box — ┌ at top-left, ┐ at top-right, ┘ at bottom-right.
 * Symmetric glyphs (`plus`, `circle`) are unaffected by rotation.
 */
const CORNER_ROTATION: Record<GridConnectorCorner, 0 | 90 | 180 | 270> = {
  "top-left": 90,
  "top-right": 180,
  "bottom-right": 270,
  "bottom-left": 0,
};

const GRID_ELBOW_FRAME_POSITION: Record<GridConnectorCorner, string> = {
  "top-left": "-top-[0.5px] -left-[0.5px]",
  "top-right": "-top-[0.5px] -right-[0.5px]",
  "bottom-left": "-bottom-[0.5px] -left-[0.5px]",
  "bottom-right": "-right-[0.5px] -bottom-[0.5px]",
};

function cornerElbowPosition(corner: GridConnectorCorner) {
  return GRID_ELBOW_FRAME_POSITION[corner];
}

function cornerStyle(corner: GridConnectorCorner, off: number) {
  switch (corner) {
    case "top-left":
      return { top: -off, left: -off };
    case "top-right":
      return { top: -off, right: -off };
    case "bottom-left":
      return { bottom: -off, left: -off };
    case "bottom-right":
      return { bottom: -off, right: -off };
  }
}
